import { parseISO, Duration, parse as parseFNS, differenceInMilliseconds, set, add as addFNS, isEqual, intervalToDuration, formatDuration, differenceInDays, differenceInHours, differenceInMinutes, differenceInSeconds } from "date-fns";
import { format as formatFNS, utcToZonedTime, zonedTimeToUtc } from "date-fns-tz";

export default class Time {
    public static dateTimeFormat = "yyyy-MM-dd HH:mm:ss";
    public static dateFormat = "yyyy-MM-dd";
    public static instanceListDateTimeURLFormat = "yyyy-MM-dd_HH:mm:ss";

    /**
     * toDetailedLocalTime converts a UTC timestamp to local detailed date time
     * @param dateTimeUtc
     * @returns local detailed date time
     */
    public static toDetailedLocalTime(dateTimeUtc?: string | Date): string {
        if (!dateTimeUtc) {
            return FRENDS.Localizations.Never;
        }
        if (dateTimeUtc instanceof Date) {
            return this.format(dateTimeUtc, Time.dateTimeFormat);
        }
        return this._fromUtcToLocalFormat(dateTimeUtc, Time.dateTimeFormat);
    }

    /**
     * toLocalDate converts a UTC timestamp to local date
     * @param dateTimeUtc
     * @returns local date
     */
    public static toLocalDate(dateTimeUtc: string): string {
        return this._fromUtcToLocalFormat(dateTimeUtc, Time.dateFormat);
    }

    /**
     * toLocalFormat converts a UTC timestamp to local with {@link timeFormat}
     * @param dateTimeUtc
     * @param timeFormat
     */
    public static toLocalFormat(dateTimeUtc: string, timeFormat: string): string {
        return this._fromUtcToLocalFormat(dateTimeUtc, timeFormat);
    }

    /**
     * toLocal converts a UTC timestamp to local date object
     * @param dateTimeUtc
     * @returns Local Date object
     */
    public static toLocal(dateTimeUtc: string): Date {
        return this._fromUtcToLocal(dateTimeUtc);
    }

    /**
     * add adds {@link duration} duration to {@link dateTime}
     * @param dateTime - local Date object 
     * @param duration
     * @returns local Date object with added {@link duration}
     */
    public static add(dateTime: Date, duration: Duration): Date {
        return this._add(dateTime, duration)
    }

    /**
     * addUTC adds {@link duration} duration to {@link dateTime}
     * @param dateTimeUtc 
     * @param duration
     * @returns UTC Date object with added {@link duration}
     */
    public static addUTC(dateTimeUtc: string, duration: Duration): Date {
        const parsed = parseISO(dateTimeUtc);
        return this._add(parsed, duration);
    }

    /**
     * format formats local {@link dateTime} into {@link format} 
     * @param dateTime 
     * @param format 
     * @returns formatted local date time
     */
    public static format(dateTime: Date, format: string): string {
        return formatFNS(dateTime, format);
    }

    /**
     * formatISO formats UTC {@link dateTime} into {@link format} 
     * @param dateTimeUtc 
     * @param format 
     * @returns formatted UTC date time
     */
    public static formatISO(dateTimeUtc: string, format: string): string {
        return this.format(utcToZonedTime(dateTimeUtc, "UTC", { timeZone: "UTC" }), format);
    }

    /**
     * set overrides {@link dateTime} values with {@link values}
     * @param dateTime 
     * @param values
     * @returns Date object with overridden values
     */
    public static set(dateTime: Date, values: SetValues): Date {
        return set(dateTime, values);
    }

    /**
     * parse parses {@link dateTime} with {@link format}
     * @param dateTime
     * @param format 
     * @return local Date object
     */
    public static parse(dateTime: string, format: string): Date {
        return parseFNS(dateTime, format, new Date());
    }

    /**
     * parseAndFormatCustom converts a custom datetime in {@link inFormat} format to {@link outFormat}
     * @param {string} dateTime
     * @param {string} inFormat
     * @param {string} outFormat  
     * @returns {string} custom datetime in {@link outFormat} format
     */
    public static parseAndFormatCustom(dateTime: string, inFormat: string, outFormat: string): string {
        return this.format(this.parse(dateTime, inFormat), outFormat);
    }

    /**
     * differenceInMilliseconds
     * @param startTimeUtc 
     * @param endTimeUtc 
     * @returns difference between {@link startTimeUtc} and {@link endTimeUtc} in milliseconds
     */
    public static differenceInMilliseconds(startTimeUtc: string, endTimeUtc: string): number {
        const parsedStartTime = parseISO(startTimeUtc);
        const parsedEndTime = parseISO(endTimeUtc);
        return differenceInMilliseconds(parsedStartTime, parsedEndTime);
    }

    /**
     * isEqual
     * @param dateTimeLeft 
     * @param dateTimeRight 
     * @returns true if both dateTime match, otherwise false
     */
    public static isEqual(dateTimeLeft: Date, dateTimeRight: Date): boolean {
        return isEqual(dateTimeLeft, dateTimeRight);
    }

    public static getInstanceListStartTime(startTimeUtcFromQuery: string): Date {
        return this._getInstanceListFilterTime(startTimeUtcFromQuery);
    }

    public static getInstanceListEndTime(endTimeUtcFromQuery: string): Date {
        return this._getInstanceListFilterTime(endTimeUtcFromQuery);
    }

    public static getDetailedDurationBetweenDates(startTime: string, endTime: string): string {
        const start = parseISO(startTime);
        const end = parseISO(endTime)

        const days = this._padTime(differenceInDays(end, start));
        const hours = this._padTime(differenceInHours(end, start) % 24);
        const mins = this._padTime(differenceInMinutes(end, start) % 60);
        const secs = this._padTime(differenceInSeconds(end, start) % 60);
        const millis = this._padTime(differenceInMilliseconds(end, start) % 1000, 3);

        return `${days}:${hours}:${mins}:${secs}.${millis}`;
    }

    public static getDurationBetweenDates(startTimeUtc: string, endTimeUtc: string, withMilliseconds: boolean = false): string {
        const startTime = this._ensureISO8601UtcFormat(startTimeUtc);
        const endTime = this._ensureISO8601UtcFormat(endTimeUtc);

        const start = parseISO(startTime);
        const end = parseISO(endTime)
        const duration = intervalToDuration({
            start: start,
            end: end
        });

        if (duration.minutes != undefined && duration.minutes < 1) {
            const milliseconds = differenceInMilliseconds(end, start) % 1000;
            if (withMilliseconds) {
                return `${duration.seconds} seconds ${milliseconds} milliseconds`;
            }
            return `${duration.seconds} seconds`;
        }

        return formatDuration(duration);
    }

    private static timeSpanRegex = /(?:(?<ne>-))?(?:(?:(?<dd>0*[0-9]+)[.])?(?:(?<HH>0*[2][0-3]|0*[1][0-9]|0*[0-9])[:]))?(?<mm>(?<=:)0*[0-5]?[0-9]|0*[5-9]?[0-9](?=[:]))(?:[:](?<ss>0*[0-5]?[0-9](?:[.][0-9]{0,7})?))?/;
    public static getDurationFromTimeSpan(durationTimeSpan: string) {
        const matches = this.timeSpanRegex.exec(durationTimeSpan)?.groups;

        if (matches == null) {
            return "";
        }

        const duration = {
            days: parseInt(matches["dd"]),
            hours: parseInt(matches["HH"]),
            minutes: parseInt(matches["mm"]),
            seconds: parseInt(matches["ss"])
        };

        if (duration.minutes < 1) {
            const seconds = matches["ss"];
            const millisecondsWithFractions = seconds.substring(seconds.indexOf(".") + 1, seconds.length);
            const milliseconds = millisecondsWithFractions.substring(0, 3);
            return `${duration.seconds} seconds ${parseInt(milliseconds)} milliseconds`;
        }

        return formatDuration(duration);
    }

    private static _padTime(unitOfTime: number, padMaxLength: number = 2) {
        return unitOfTime.toString().padStart(padMaxLength, "0");
    }

    private static _getInstanceListFilterTime(filterTimeUtc: string): Date {
        const filterTime = new Date(zonedTimeToUtc(this.parse(filterTimeUtc, Time.instanceListDateTimeURLFormat), "UTC").toISOString());
        return filterTime;
    }

    private static _fromUtcToLocal(dateTimeUtc: string): Date {
        const parsedDateTime = parseISO(dateTimeUtc);
        const localTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const localDateTime = utcToZonedTime(parsedDateTime, localTimeZone);
        return localDateTime;
    }

    private static _fromUtcToLocalFormat(dateTimeUtc: string, timeFormat: string): string {
        const normalizedDateTimeUtc = this._ensureISO8601UtcFormat(dateTimeUtc);
        const localDateTime = this._fromUtcToLocal(normalizedDateTimeUtc);
        return this.format(localDateTime, timeFormat);
    }

    private static _add(dateTime: Date, duration: Duration): Date {
        return addFNS(dateTime, duration);
    }

    private static _ensureISO8601UtcFormat(dateTimeUtc: string): string {
        // In some cases the returned date from the backend doesn't include Z which is required for date-fns
        let normalizedDateTimeUtc = dateTimeUtc.toUpperCase();
        if (normalizedDateTimeUtc.includes("T") && !normalizedDateTimeUtc.endsWith("Z")) {
            normalizedDateTimeUtc += "Z";
        }
        return normalizedDateTimeUtc;
    }
}

interface SetValues {
    year?: number
    month?: number
    date?: number
    hours?: number
    minutes?: number
    seconds?: number
    milliseconds?: number
}
